/* eslint-disable no-console */
type SwipeDirectionX = 'left' | 'right'

type SwipeDirectionY = 'up' | 'down'

type SwipeDirection = SwipeDirectionX | SwipeDirectionY
interface useTouchProps {
  onTap?: (e: TouchEvent) => void
  onDoubleTap?: (e: TouchEvent) => void
  debug?: boolean
  onSwipe?: (direction: SwipeDirection) => void
  onSwipeX?: (direction: SwipeDirectionX) => void
  onSwipeY?: (direction: SwipeDirectionY) => void
  onHold?: (e: TouchEvent) => void
  holdDuration?: number
}

interface TouchData {
  duration: number
  directionX: SwipeDirectionX
  directionY: SwipeDirectionY
  distanceX: number
  distanceY: number
  raw: {
    distanceX: number
    distanceY: number
  }
}

interface useTouchReturn {
  onTouchStart: (e: TouchEvent) => void
  onTouchMove: (e: TouchEvent) => void
  onTouchEnd: (e: TouchEvent) => void
}

const DOUBLE_TAP_THRESHOLD_DURATION = 300 // ms

const TAP_THRESHOLD_DURATION = 100 // ms

const SWIPE_THRESHOLD_DISTANCE = 30 // px

const HOLD_THRESHOLD_DURATION = 1000 // ms

export function useTouch(
  props: useTouchProps = {
    onTap: undefined,
    onDoubleTap: undefined,
    debug: false,
    onSwipe: undefined,
    onSwipeX: undefined,
    onSwipeY: undefined,
    onHold: undefined,
    holdDuration: HOLD_THRESHOLD_DURATION,
  },
): useTouchReturn {
  const isTouching = ref(false)
  const touchStartDateTime = ref(0)
  const touchStartX = ref(0)
  const touchStartY = ref(0)
  const touchEndX = ref(0)
  const touchEndY = ref(0)
  const currentTouchesCount = ref(0)

  const timeoutSignal = ref<NodeJS.Timeout | null>(null)

  const reset = () => {
    isTouching.value = false
    touchStartDateTime.value = 0
    touchStartX.value = 0
    touchStartY.value = 0
    touchEndX.value = 0
    touchEndY.value = 0
    currentTouchesCount.value = 0

    if (props.debug)
      console.log('reset')
  }

  const dispatchTap = (e: TouchEvent) => {
    if (props.debug)
      console.log('dispatchTap')
    props.onTap?.(e)
    reset()
  }
  const dispatchDoubleTap = (e: TouchEvent) => {
    if (props.debug)
      console.log('dispatchDoubleTap')

    props.onDoubleTap?.(e)
    reset()
  }

  const dispatchSwipe = (direction: SwipeDirection) => {
    if (timeoutSignal.value) {
      clearTimeout(timeoutSignal.value)
      timeoutSignal.value = null
    }
    if (props.debug)
      console.log('dispatchSwipe', direction)

    props.onSwipe?.(direction)
    if (direction === 'left' || direction === 'right')
      props.onSwipeX?.(direction)
    else
      props.onSwipeY?.(direction)

    reset()
  }

  const disapatchHold = (e: TouchEvent) => {
    if (props.debug)
      console.log('disapatchHold', props.holdDuration)

    props.onHold?.(e)
    reset()
  }

  const onTouchStart = (e: TouchEvent) => {
    if (timeoutSignal.value) {
      clearTimeout(timeoutSignal.value)
      timeoutSignal.value = null
    }
    isTouching.value = true
    touchStartX.value = e.touches[0].clientX
    touchStartY.value = e.touches[0].clientY
    touchStartDateTime.value = e.timeStamp
    currentTouchesCount.value = currentTouchesCount.value + 1
    timeoutSignal.value = setTimeout(() => {
      disapatchHold(e)
    }, props.holdDuration ?? HOLD_THRESHOLD_DURATION)
  }

  const onTouchMove = (e: TouchEvent) => {
    touchEndX.value = e.touches[0].clientX
    touchEndY.value = e.touches[0].clientY
  }

  const onTouchEnd = (e: TouchEvent) => {
    isTouching.value = false
    const duration = e.timeStamp - touchStartDateTime.value
    const distanceX = touchEndX.value - touchStartX.value
    const distanceY = touchEndY.value - touchStartY.value
    const directionInfo: TouchData = {
      duration,
      directionX: distanceX > 0 ? 'right' : 'left',
      directionY: distanceY > 0 ? 'down' : 'up',
      distanceX: Math.abs(distanceX),
      distanceY: Math.abs(distanceY),
      raw: {
        distanceX,
        distanceY,
      },
    }

    if (props.debug)
      console.log(directionInfo)

    if (distanceX > SWIPE_THRESHOLD_DISTANCE || distanceY > SWIPE_THRESHOLD_DISTANCE) {
      if (distanceX > SWIPE_THRESHOLD_DISTANCE && distanceY < SWIPE_THRESHOLD_DISTANCE)
        dispatchSwipe(directionInfo.directionX)
      else if (distanceY > SWIPE_THRESHOLD_DISTANCE && distanceX < SWIPE_THRESHOLD_DISTANCE)
        dispatchSwipe(directionInfo.directionY)
      return
    }

    if (duration < TAP_THRESHOLD_DURATION && currentTouchesCount.value === 1) {
      if (timeoutSignal.value) {
        clearTimeout(timeoutSignal.value)
        timeoutSignal.value = null
      }
      timeoutSignal.value = setTimeout(() => {
        dispatchTap(e)
      }, TAP_THRESHOLD_DURATION)
    }
    else if (duration < DOUBLE_TAP_THRESHOLD_DURATION && currentTouchesCount.value > 1) {
      if (timeoutSignal.value) {
        clearTimeout(timeoutSignal.value)
        timeoutSignal.value = null
      }
      timeoutSignal.value = setTimeout(() => {
        dispatchDoubleTap(e)
      }, DOUBLE_TAP_THRESHOLD_DURATION)
    }
  }

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  }
}
